import { X, CheckCircle, Info, AlertTriangle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAnnouncementStore, useVisibleAnnouncement } from '../../store/announcementStore';
import { localizeTimes } from '../../utils/announcementTemplate';
import type { AnnouncementLevel } from '../../types';

const icons: Record<AnnouncementLevel, typeof Info> = {
  success: CheckCircle,
  info: Info,
  warning: AlertTriangle,
  danger: AlertCircle,
};

/**
 * Choisit la version à afficher selon la langue de l'interface.
 *
 * Le français fait office de langue pivot : une annonce dont la traduction
 * anglaise n'a pas été saisie s'affiche en français plutôt que de disparaître.
 */
export function pickContent(
  language: string,
  contentFr: string,
  contentEn: string
): string {
  if (language.startsWith('en') && contentEn.trim()) return contentEn;
  return contentFr;
}

/**
 * Bandeau d'annonce, en flux au-dessus du contenu de page.
 *
 * Volontairement pas `fixed` : il pousse la page vers le bas au lieu de la
 * recouvrir, ce qui évite de masquer la première ligne de contenu et les
 * en-têtes collants.
 */
export default function AnnouncementBanner() {
  const { t, i18n } = useTranslation('common');
  const announcement = useVisibleAnnouncement();
  const dismiss = useAnnouncementStore((state) => state.dismiss);

  if (!announcement) return null;

  const Icon = icons[announcement.level];
  const language = i18n.language.startsWith('en') ? 'en' : 'fr';
  const raw = pickContent(i18n.language, announcement.contentFr, announcement.contentEn);

  if (!raw.trim()) return null;

  // Les horaires publiés sont convertis ici, dans le fuseau du lecteur.
  const html = localizeTimes(raw, language);

  return (
    <div className="mb-4">
      <div
        className="announcement-banner"
        data-level={announcement.level}
        role={announcement.level === 'danger' ? 'alert' : 'status'}
      >
        <Icon className="announcement-banner__icon w-5 h-5" aria-hidden="true" />
        <div
          className="announcement-banner__content"
          // Contenu rédigé par un administrateur et assaini côté serveur
          // (allow-list de balises de mise en forme, cf. sanitizeAnnouncementHtml).
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <button
          type="button"
          onClick={dismiss}
          className="announcement-banner__close"
          aria-label={t('announcement.dismiss')}
          title={t('announcement.dismiss')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
